import React, { useRef, useEffect, useCallback } from 'react'
import styled, { css } from 'styled-components'
import GatsbyImage from 'gatsby-image'
import loadable from '@loadable/component'
import * as R from 'ramda'
import { useSpring, animated, interpolate } from 'react-spring'
import { useEventListener } from '../../hooks/useEventListener'

const RichText = loadable(() => import('../RichText'))

const TechnologyIntroduction = ({
  introductionHighlights,
  introductionCopy,
  introductionCopyTwo,
  introductionImage,
}) => {
  const highlightEl = useRef()

  const [{ springscrollY }, springsetScrollY] = useSpring(() => ({
    springscrollY: 0,
  }))

  const handler = useCallback(() => {
    const { top, height } = highlightEl.current.getBoundingClientRect()
    const length = height + window.innerHeight + 100
    const breakpoint = 1023
    const percentage = R.clamp(0, 1)((top + height) / length)
    const offSet =
      window.innerWidth >= breakpoint
        ? (percentage * 100 - 50) / 3
        : percentage * 100 - 50

    springsetScrollY({ springscrollY: offSet })
  }, [springsetScrollY])

  useEventListener('scroll', handler)

  const horizontalScroll = springscrollY.interpolate((o) => `translateX(${o}%)`)

  return (
    <StyledIntroduction>
      <Highlights ref={highlightEl} style={{ transform: horizontalScroll }}>
        {introductionHighlights.map((item) => (
          <Highlight key={item}>{item}</Highlight>
        ))}
      </Highlights>
      <Row>
        <Col copy>
          <Copy>
            <RichText json={introductionCopy} />
            <RichText json={introductionCopyTwo} />
          </Copy>
        </Col>
        <Col photo>
          <StyledImage
            fluid={introductionImage.fluid}
            alt={introductionImage.title}
            withPlaceholder
          />
        </Col>
      </Row>
    </StyledIntroduction>
  )
}

const StyledIntroduction = styled.section`
  overflow: hidden;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    overflow: unset;
  }
`

const Highlights = styled(animated.ul)`
  display: flex;
  flex-wrap: wrap;
  overflow: none;
  padding-top: ${(props) => props.theme.space.marginMobile * 6}vw;
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  padding-left: ${(props) => props.theme.space.marginMobile * 3}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    justify-content: center;
    padding-top: ${(props) => props.theme.space.marginDesktop * 12}vw;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 6}vw;
    text-align: center;
  }
`

const Highlight = styled.li`
  font-size: 7.334vw;
  white-space: nowrap;
  padding: 0;
  width: 50%;
  margin: ${(props) => props.theme.space.marginMobile}vw 0;
  text-align: center;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin: 0;
    width: auto;
    font-size: ${(props) => props.theme.fontSize.d35};
    padding-right: ${(props) => props.theme.space.marginDesktop * 4}vw;
  }
`

const Copy = styled.div`
  line-height: 4.8vw;
  letter-spacing: 0.05em;
  padding: ${(props) => props.theme.space.marginMobile * 4}vw
    ${(props) => props.theme.space.marginMobile * 4}vw
    ${(props) => props.theme.space.marginMobile * 4}vw;

  ${(props) =>
    props.desktop &&
    css`
      display: none;
    `}

  p {
    margin-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: ${(props) => (props.desktop ? 'none' : 'block')};
    padding: 0 ${(props) => props.theme.space.marginDesktop * 10}vw
      ${(props) => props.theme.space.marginDesktop * 2}vw;
    line-height: 1.5277777778vw;

    flex: 1;
    ${(props) =>
      props.desktop &&
      css`
        display: block;
      `}

    ${(props) =>
      props.mobile &&
      css`
        display: none;
      `}


    p {
      margin-bottom: ${(props) => props.theme.space.marginDesktop * 3}vw;
    }
  }

  h4 {
    font-family: ${(props) => props.theme.fontFamily.bold};
    margin-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;
    font-size: 3.733334vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d15};
      margin-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    }
  }

  h5 {
    font-family: ${(props) => props.theme.fontFamily.bold};
    margin-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;
    font-size: ${(props) => props.theme.fontSize.m23};
    text-transform: uppercase;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d12};
      margin-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    }
  }

  p {
    font-size: ${(props) => props.theme.fontSize.m23};

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d12};
    }
  }
`

const Row = styled.div`
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: flex;
    flex-direction: row-reverse;
    padding: ${(props) => props.theme.space.marginDesktop * 6}vw
      ${(props) => props.theme.space.marginDesktop * 16}vw
      ${(props) => props.theme.space.marginDesktop * 12}vw;
  }
`

const Col = styled.div`
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    flex: 1;
    ${(props) =>
      props.copy &&
      css`
        padding-top: ${props.theme.space.marginDesktop * 24}vw;
        padding-bottom: ${props.theme.space.marginDesktop * 16}vw;
      `}
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    flex: 1 0 50%;
    display: ${(props) => (props.photo ? 'flex' : 'block')};
    flex-direction: ${(props) => (props.photo ? 'column' : 'row-reverse')};
    justify-content: ${(props) => (props.photo ? 'center' : 'flex-start')};
    height: auto;
  }
`

const StyledImage = styled(GatsbyImage)`
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    flex: 1;
    top: ${(props) => props.theme.space.marginDesktop * 10}vw;
    position: sticky !important;
    margin: 0 ${(props) => props.theme.space.marginDesktop * 2}vw;
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    height: auto;
  }
`

export default TechnologyIntroduction
